import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import {
    Button,
    LoaderPage,
    RegionState,
    useAuthentication,
    useI18n,
    useSupport,
    useTracking,
} from '@freelancelabs/components';
import { AppName, consommerSortie, marquerSortie, redirectToApp } from '@freelancelabs/shared';

import { useMfaGate } from '../domains/shared/hooks/useMfaGate';
import useViewerAttachment from '../domains/shared/hooks/useViewerAttachment';
import supportEvents from '../events/support';
import trackEvents from '../events/tracking';
import { ViewerDocument } from '../graphql/__generated__/types';
import AppRoutesTree from '../routes';

const Setup = () => {
    const { isInitialized: isInitializedAuth, hasSession, logout } = useAuthentication();
    const { isInitialized: isInitializedI18n } = useI18n();
    const { data } = useQuery(ViewerDocument);

    const viewer = data?.viewer;
    const membership = viewer?.memberships?.find(({ isDefault }) => isDefault) ?? viewer?.memberships?.[0];

    const userData = {
        id: viewer?.id ?? undefined,
        email: viewer?.email ?? undefined,
        firstName: viewer?.firstName,
        lastName: viewer?.lastName,
        customerPortalRole: membership?.role?.name,
    };
    useTracking(trackEvents, userData);
    useSupport(supportEvents, userData);
    const mfa = useMfaGate(viewer?.mfaRequired);
    const attachment = useViewerAttachment();

    // Eject only when the attachment is known-missing, never on loading or failed.
    const sansRattachement = attachment.status === 'unattached';
    const [sortieEnEchec, setSortieEnEchec] = React.useState(false);
    React.useEffect(() => {
        if (!sansRattachement || sortieEnEchec) {
            return;
        }
        // Don't redirect here: it races the hasSession-false branch below.
        marquerSortie('APP_UNAUTHORIZED');
        void logout().catch(() => setSortieEnEchec(true));
    }, [sansRattachement, sortieEnEchec, logout]);

    if (!isInitializedAuth || !isInitializedI18n) {
        return <LoaderPage />;
    }

    if (isInitializedAuth && isInitializedI18n && !hasSession) {
        const motif = consommerSortie();
        redirectToApp(AppName.ACCOUNT, motif ? { alerte: motif } : { 'redirect-after-login': window.location.href });
        return <LoaderPage />;
    }

    // A link, not an auto-redirect, to avoid a bounce loop when account fails open.
    if (sansRattachement && !sortieEnEchec) {
        return <LoaderPage />;
    }

    if (mfa.blocked) {
        return (
            <main className="flex flex-1 h-full flex-col items-center justify-center gap-6 p-6">
                <RegionState
                    title={c('Mfa required').t`Two-factor authentication is required`}
                    description={c('Mfa required')
                        .t`Your organisation requires it before you use your space. Set up an authenticator app once, and a code from it will be asked for at each sign-in afterwards.`}
                >
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <Button
                            onClick={() =>
                                redirectToApp(AppName.ACCOUNT, {
                                    'redirect-after-login': window.location.href,
                                })
                            }
                        >
                            {c('Mfa required action').t`Set it up now`}
                        </Button>
                        <Button onClick={logout} category="weak">
                            {c('Action').t`Log out`}
                        </Button>
                    </div>
                </RegionState>
            </main>
        );
    }

    return <AppRoutesTree />;
};

export default Setup;
